import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PillToggle } from "@/components/admin/PillToggle";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, GripVertical, Upload, X, FolderOpen, Shuffle, Eye, EyeOff, Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MediaPicker } from "@/components/admin/MediaPicker";
import { MediaPortfolioManager } from "@/components/admin/MediaPortfolioManager";
import { TagSelector } from "@/components/admin/TagSelector";
import { mapDatabaseError } from "@/lib/errorUtils";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type Talent = {
  id: string;
  name: string;
  slug: string;
  specialty: string;
  bio: string | null;
  image_url: string | null;
  languages: string[];
  is_featured: boolean;
  is_active: boolean;
  display_order: number;
};

const emptyTalent = {
  name: "",
  slug: "",
  specialty: "",
  bio: "",
  image_url: "",
  languages: [] as string[],
  is_featured: false,
  is_active: true,
  display_order: 0,
};

// Sortable row component
interface SortableTalentRowProps {
  talent: Talent;
  onEdit: (talent: Talent) => void;
  onDelete: (id: string) => void;
  onToggleVisibility: (id: string, isActive: boolean) => void;
}

const SortableTalentRow = ({ talent, onEdit, onDelete, onToggleVisibility }: SortableTalentRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: talent.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} className={isDragging ? "z-50" : ""}>
      <CardContent className="flex items-center gap-4 p-4">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing touch-none"
        >
          <GripVertical className="text-muted-foreground" />
        </div>
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center overflow-hidden">
          {talent.image_url ? (
            <img
              src={talent.image_url}
              alt={talent.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <span className="text-muted-foreground text-lg font-medium">
              {talent.name.charAt(0)}
            </span>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className={`font-medium ${!talent.is_active ? "text-muted-foreground" : ""}`}>{talent.name}</h3>
            {!talent.is_active && (
              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                Hidden
              </span>
            )}
            {talent.is_featured && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                Featured
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{talent.specialty}</p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onToggleVisibility(talent.id, !talent.is_active)}
            title={talent.is_active ? "Hide talent" : "Show talent"}
          >
            {talent.is_active ? <Eye size={16} /> : <EyeOff size={16} className="text-muted-foreground" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(talent)}
          >
            <Pencil size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (confirm("Delete this talent?")) {
                onDelete(talent.id);
              }
            }}
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const TalentAdmin = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTalent, setEditingTalent] = useState<Talent | null>(null);
  const [formData, setFormData] = useState(emptyTalent);
  const [languageTags, setLanguageTags] = useState<string[]>([]);
  const [specialtyTags, setSpecialtyTags] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleImageUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `talent/${formData.slug || Date.now()}-${Date.now()}.${fileExt}`;
      
      // Delete old image if replacing
      if (formData.image_url?.includes("media")) {
        const oldPath = formData.image_url.split("/media/")[1];
        if (oldPath) {
          await supabase.storage.from("media").remove([oldPath]);
        }
      }

      const { error: uploadError } = await supabase.storage
        .from("media")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("media")
        .getPublicUrl(fileName);

      setFormData({ ...formData, image_url: publicUrl });
      toast.success("Image uploaded");
    } catch (error: unknown) {
      toast.error(mapDatabaseError(error));
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageDelete = async () => {
    if (!formData.image_url) return;
    
    try {
      if (formData.image_url.includes("media")) {
        const path = formData.image_url.split("/media/")[1];
        if (path) {
          await supabase.storage.from("media").remove([path]);
        }
      }
      setFormData({ ...formData, image_url: "" });
      toast.success("Image removed");
    } catch (error: unknown) {
      toast.error(mapDatabaseError(error));
    }
  };

  const { data: talent, isLoading } = useQuery({
    queryKey: ["admin-talent"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("talent")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data as Talent[];
    },
  });

  // Fetch random featured setting
  const { data: randomFeaturedSetting } = useQuery({
    queryKey: ["random-featured-setting"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "random_featured_talent")
        .maybeSingle();
      if (error) throw error;
      return (data?.value as { enabled: boolean })?.enabled ?? false;
    },
  });

  const randomFeaturedMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data: existing } = await supabase
        .from("site_settings")
        .select("id")
        .eq("key", "random_featured_talent")
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("site_settings")
          .update({ value: { enabled } })
          .eq("key", "random_featured_talent");
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("site_settings")
          .insert({ key: "random_featured_talent", value: { enabled } });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["random-featured-setting"] });
      queryClient.invalidateQueries({ queryKey: ["featured-talent"] });
      toast.success(randomFeaturedSetting ? "Using manual featured talents" : "Using random featured talents");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  // Fetch specialties from the master specialties table
  const { data: existingSpecialties = [] } = useQuery({
    queryKey: ["talent-specialties"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("specialties")
        .select("name")
        .order("name");
      
      if (error) throw error;
      return data?.map((s) => s.name) || [];
    },
  });

  // Fetch existing languages for tag suggestions
  const { data: existingLanguages = [] } = useQuery({
    queryKey: ["talent-languages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("talent")
        .select("languages")
        .not("languages", "is", null);
      
      if (error) throw error;
      
      // Flatten and get unique languages
      const allLanguages = data?.flatMap((t) => t.languages || []) || [];
      const uniqueLanguages = [...new Set(allLanguages)].sort();
      return uniqueLanguages as string[];
    },
  });

  // Handle edit query parameter from talent page
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && talent) {
      const talentToEdit = talent.find((t) => t.id === editId);
      if (talentToEdit) {
        handleEdit(talentToEdit);
        // Clear the query param after opening
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, talent]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData & { id?: string }) => {
      const payload = {
        ...data,
        languages: data.languages,
      };

      if (data.id) {
        const { error } = await supabase
          .from("talent")
          .update(payload)
          .eq("id", data.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("talent").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-talent"] });
      queryClient.invalidateQueries({ queryKey: ["talent-specialties"] });
      queryClient.invalidateQueries({ queryKey: ["talent-languages"] });
      setIsDialogOpen(false);
      setEditingTalent(null);
      setFormData(emptyTalent);
      setLanguageTags([]);
      setSpecialtyTags([]);
      toast.success(editingTalent ? "Talent updated" : "Talent created");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("talent").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-talent"] });
      toast.success("Talent deleted");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (updates: { id: string; display_order: number }[]) => {
      for (const update of updates) {
        const { error } = await supabase
          .from("talent")
          .update({ display_order: update.display_order })
          .eq("id", update.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-talent"] });
      toast.success("Order updated");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const visibilityMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from("talent")
        .update({ is_active: isActive })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-talent"] });
      queryClient.invalidateQueries({ queryKey: ["featured-talent"] });
      toast.success(isActive ? "Talent is now visible" : "Talent is now hidden");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const translateMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await supabase.functions.invoke("translate-talent", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-talent"] });
      toast.success(data?.message || "Translation completed");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id && talent) {
      const oldIndex = talent.findIndex((t) => t.id === active.id);
      const newIndex = talent.findIndex((t) => t.id === over.id);

      const newOrder = arrayMove(talent, oldIndex, newIndex);
      
      // Optimistically update the UI
      queryClient.setQueryData(["admin-talent"], newOrder);

      // Update display_order for all affected items
      const updates = newOrder.map((t, index) => ({
        id: t.id,
        display_order: index,
      }));

      reorderMutation.mutate(updates);
    }
  };

  const handleEdit = (t: Talent) => {
    setEditingTalent(t);
    setFormData({
      name: t.name,
      slug: t.slug,
      specialty: t.specialty,
      bio: t.bio || "",
      image_url: t.image_url || "",
      languages: t.languages || [],
      is_featured: t.is_featured,
      is_active: t.is_active,
      display_order: t.display_order,
    });
    setLanguageTags(t.languages || []);
    setSpecialtyTags(t.specialty ? t.specialty.split(",").map(s => s.trim()).filter(Boolean) : []);
    setIsDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingTalent(null);
    setFormData(emptyTalent);
    setLanguageTags([]);
    setSpecialtyTags([]);
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Combine specialty tags into comma-separated string
    const specialty = specialtyTags.join(", ");
    
    saveMutation.mutate({
      ...formData,
      specialty,
      languages: languageTags,
      id: editingTalent?.id,
    });
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold">Talent</h1>
          <p className="text-muted-foreground mt-1">
            Manage voice-over talent profiles. Drag to reorder.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => translateMutation.mutate()}
                  disabled={translateMutation.isPending}
                >
                  {translateMutation.isPending ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <Languages size={16} className="mr-2" />
                  )}
                  {translateMutation.isPending ? "Translating..." : "Translate to French"}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Generate French translations for all talent bios and specialties</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 bg-muted/50 px-3 py-1.5 rounded-lg">
                  <Shuffle size={16} className={randomFeaturedSetting ? "text-primary" : "text-muted-foreground"} />
                  <Label className="text-sm text-muted-foreground whitespace-nowrap">Random Featured</Label>
                  <PillToggle
                    checked={randomFeaturedSetting ?? false}
                    onCheckedChange={(checked) => randomFeaturedMutation.mutate(checked)}
                    labelOn="On"
                    labelOff="Off"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{randomFeaturedSetting ? "Showing 4 random talents on homepage" : "Showing manually selected featured talents"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleCreate}>
                <Plus size={18} className="mr-2" />
                Add Talent
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
              <DialogTitle>
                {editingTalent ? "Edit Talent" : "Add New Talent"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ 
                        ...formData, 
                        name: e.target.value,
                        slug: formData.slug || generateSlug(e.target.value)
                      });
                    }}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Specialty *</Label>
                <TagSelector
                  value={specialtyTags}
                  onChange={setSpecialtyTags}
                  availableTags={existingSpecialties}
                  placeholder="Select specialties..."
                  allowCustom={false}
                />
                {specialtyTags.length === 0 && (
                  <p className="text-xs text-muted-foreground">At least one specialty is required</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) =>
                    setFormData({ ...formData, bio: e.target.value })
                  }
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label>Languages</Label>
                <TagSelector
                  value={languageTags}
                  onChange={setLanguageTags}
                  availableTags={existingLanguages}
                  placeholder="Select or add languages..."
                  allowCustom={true}
                />
              </div>

              <div className="space-y-2">
                <Label>Profile Image</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleImageUpload(file);
                  }}
                />
                {formData.image_url ? (
                  <div className="relative w-32 h-32">
                    <img
                      src={formData.image_url}
                      alt="Preview"
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-6 w-6"
                      onClick={handleImageDelete}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      <Upload size={16} className="mr-2" />
                      {isUploading ? "Uploading..." : "Upload"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsMediaPickerOpen(true)}
                    >
                      <FolderOpen size={16} className="mr-2" />
                      Browse Library
                    </Button>
                  </div>
                )}
                {formData.image_url && (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      Replace
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsMediaPickerOpen(true)}
                    >
                      Browse Library
                    </Button>
                  </div>
                )}
                <MediaPicker
                  open={isMediaPickerOpen}
                  onOpenChange={setIsMediaPickerOpen}
                  onSelect={(url) => setFormData({ ...formData, image_url: url })}
                  bucket="media"
                  folder="talent"
                />
              </div>

              {/* Media Portfolio - only show when editing existing talent */}
              {editingTalent && (
                <div className="border-t pt-4">
                  <MediaPortfolioManager talentId={editingTalent.id} />
                </div>
              )}

              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-muted-foreground">Visible</Label>
                  <PillToggle
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_active: checked })
                    }
                    labelOn="Yes"
                    labelOff="No"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-muted-foreground">Featured</Label>
                  <PillToggle
                    checked={formData.is_featured}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_featured: checked })
                    }
                    labelOn="Yes"
                    labelOff="No"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="display_order">Order</Label>
                  <Input
                    id="display_order"
                    type="number"
                    value={formData.display_order}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        display_order: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-20"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? "Saving..." : "Save"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : talent && talent.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={talent.map((t) => t.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {talent.map((t) => (
                <SortableTalentRow
                  key={t.id}
                  talent={t}
                  onEdit={handleEdit}
                  onDelete={(id) => deleteMutation.mutate(id)}
                  onToggleVisibility={(id, isActive) => visibilityMutation.mutate({ id, isActive })}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No talent added yet.</p>
            <Button onClick={handleCreate} className="mt-4">
              <Plus size={18} className="mr-2" />
              Add Your First Talent
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TalentAdmin;